import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, ShoppingCart, Coffee, Home,
  Fuel, Phone, Heart, UtensilsCrossed, Shirt, Bus,
  Check, Star,
} from 'lucide-react';

// ─── Small Wins ─────────────────────────────────────────────────────
// Every upsell = real money. Not abstract. Not "points."
// This screen shows exactly what each small win buys you in real life.
// Because when you're counting pennies, "$6.80" needs to mean something.

interface NeedItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  cost: number;
  color: string;
}

const NEEDS: NeedItem[] = [
  { icon: UtensilsCrossed, label: 'Tomorrow\'s meals', cost: 15, color: 'text-orange-500' },
  { icon: Coffee, label: 'Week of morning coffee', cost: 25, color: 'text-amber-600' },
  { icon: Bus, label: 'Week of bus fare', cost: 20, color: 'text-blue-500' },
  { icon: Fuel, label: 'Tank of gas', cost: 50, color: 'text-gray-500' },
  { icon: ShoppingCart, label: 'Full grocery run', cost: 80, color: 'text-emerald-500' },
  { icon: Phone, label: 'Phone bill', cost: 85, color: 'text-indigo-500' },
  { icon: Shirt, label: 'New work shoes', cost: 120, color: 'text-purple-500' },
  { icon: Home, label: 'Week toward rent', cost: 200, color: 'text-red-500' },
];

interface UpsellEvent {
  time: string;
  item: string;
  amount: number;
}

const TONIGHT_UPSELLS: UpsellEvent[] = [
  { time: '5:18 PM', item: 'Wagyu Platter upsell', amount: 6.80 },
  { time: '5:22 PM', item: '2x Mango Lassi', amount: 3.20 },
  { time: '5:41 PM', item: 'Döner upgrade', amount: 1.60 },
  { time: '6:04 PM', item: 'Wagyu Platter upsell', amount: 6.80 },
  { time: '6:07 PM', item: '3x Mango Lassi', amount: 4.80 },
  { time: '6:10 PM', item: 'Dubai Chocolate', amount: 2.80 },
  { time: '6:48 PM', item: 'Wagyu Platter (table of 4)', amount: 13.60 },
  { time: '6:52 PM', item: '4x drinks', amount: 6.40 },
  { time: '7:15 PM', item: 'Döner upgrade', amount: 1.60 },
  { time: '7:18 PM', item: 'Dubai Chocolate', amount: 2.80 },
];

const SmallWins: React.FC = () => {
  const navigate = useNavigate();
  const [shownCount, setShownCount] = useState(TONIGHT_UPSELLS.length);

  const totalExtra = TONIGHT_UPSELLS.slice(0, shownCount).reduce((s, e) => s + e.amount, 0);
  const runningTotal: number[] = [];
  TONIGHT_UPSELLS.slice(0, shownCount).forEach((e, i) => {
    runningTotal.push((runningTotal[i - 1] || 0) + e.amount);
  });

  // Figure out which needs are covered
  const coveredNeeds = NEEDS.filter(n => n.cost <= totalExtra);
  const nextNeed = NEEDS.find(n => n.cost > totalExtra);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Small Wins</h1>
          <p className="text-sm text-gray-500">Every upsell tonight turned into something real.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* The big number with meaning */}
        <div className="text-center py-4">
          <div className="text-xs text-gray-400 uppercase tracking-widest mb-2">Extra money from upsells tonight</div>
          <div className="text-5xl font-bold text-gray-900 mb-2">${totalExtra.toFixed(2)}</div>
          <p className="text-sm text-gray-500">
            This is money that wouldn't exist if you hadn't spoken up.
          </p>
        </div>

        {/* What it buys — visual progress */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">What that ${totalExtra.toFixed(0)} covers</h2>
          <div className="space-y-3">
            {NEEDS.map((need, i) => {
              const pct = Math.min((totalExtra / need.cost) * 100, 100);
              const covered = totalExtra >= need.cost;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    covered ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    {covered ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <need.icon className={`w-4 h-4 ${need.color}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-sm ${covered ? 'text-emerald-700 font-medium' : 'text-gray-700'}`}>
                        {need.label}
                      </span>
                      <span className="text-xs text-gray-400">${need.cost}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${covered ? 'bg-emerald-400' : 'bg-gray-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {nextNeed && (
            <div className="mt-4 pt-3 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">${(nextNeed.cost - totalExtra).toFixed(2)}</span> more to cover {nextNeed.label.toLowerCase()}.
                That's {Math.ceil((nextNeed.cost - totalExtra) / 6.8)} more Wagyu upsells.
              </p>
            </div>
          )}
        </div>

        {/* Each upsell as a moment */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Every upsell tonight</h2>
          <div className="space-y-2">
            {TONIGHT_UPSELLS.slice(0, shownCount).map((event, i) => {
              const running = runningTotal[i];
              // Find the first need this running total newly covers
              const newlyCovered = NEEDS.find(n => n.cost <= running && (i === 0 || n.cost > runningTotal[i - 1]));
              return (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-3 h-3 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-700">{event.item}</div>
                      <div className="text-xs text-gray-400">{event.time}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-emerald-600">+${event.amount.toFixed(2)}</div>
                      <div className="text-[10px] text-gray-400">total: ${running.toFixed(2)}</div>
                    </div>
                  </div>
                  {newlyCovered && (
                    <div className="flex items-center gap-2 px-4 py-2 ml-8">
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="text-xs text-emerald-700 font-medium">
                        {newlyCovered.label} — covered
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* The comparison that matters */}
        <div className="bg-gray-900 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-white mb-4">Two versions of tonight</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-2">If you didn't upsell</div>
              <div className="text-2xl font-bold text-gray-400">$114.50</div>
              <div className="text-xs text-gray-600 mt-1">Base tips only</div>
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Check className="w-3 h-3" /> Tomorrow's meals
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-3 h-3 flex-shrink-0" /> <span className="line-through">Bus fare</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-3 h-3 flex-shrink-0" /> <span className="line-through">Groceries</span>
                </div>
              </div>
            </div>
            <div className="bg-emerald-900/50 border border-emerald-700/50 rounded-lg p-4">
              <div className="text-xs text-emerald-400 mb-2">With your upsells</div>
              <div className="text-2xl font-bold text-white">${(114.50 + totalExtra).toFixed(2)}</div>
              <div className="text-xs text-emerald-400 mt-1">Base + ${totalExtra.toFixed(2)} extra</div>
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <Check className="w-3 h-3" /> Tomorrow's meals
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <Check className="w-3 h-3" /> Bus fare
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <Check className="w-3 h-3" /> Coffee for the week
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">
            Same shift. Same tables. Same hours. Different words. Different life.
          </p>
        </div>

        {/* Closing */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">
            Small wins aren't small when you need them.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmallWins;
